import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { endpoints } from '../../api';
import type { Voice } from '../../api/types';
import { uiText } from '../../i18n';
import { StudioIcon } from '../studio/StudioIcon';

/** Таблетка голоса формы: выбор открывается шаблонным списком поверх раздела (BotCore.voiceHost.page) — тем же, что у вкладки «Озвучка»;
    s2s — только голоса с переозвучкой. Список отдаёт id, полные сведения о голосе подтверждает сервер */
export function VoicePick({
  value,
  s2s = false,
  disabled = false,
  sub,
  onPick,
}: {
  value: Voice | null;
  s2s?: boolean;
  disabled?: boolean;
  sub?: string;
  onPick(voice: Voice): void;
}) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  const request = useRef<AbortController | null>(null);
  const disabledRef = useRef(disabled);
  useLayoutEffect(() => { disabledRef.current = disabled; if (disabled) request.current?.abort(); }, [disabled]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; request.current?.abort(); disabledRef.current = true; }; }, []);
  function open() {
    if (disabledRef.current) return;
    setError('');
    window.BotCore.voiceHost.page(
      id => {
        if (disabledRef.current) return;
        request.current?.abort();
        const current = new AbortController(); request.current = current;
        setBusy(true);
        void endpoints
          .voiceDetails(id, current.signal)
          .then(voice => {
            if (current.signal.aborted || request.current !== current || disabledRef.current) return;
            if (voice.id !== id) throw new Error('voice_mismatch');
            if (s2s && !voice.is_s2s) throw new Error('voice_not_s2s');
            onPick(voice);
          })
          .catch(failure => { if (!current.signal.aborted && request.current === current) setError(failure instanceof Error && failure.message === 'voice_not_s2s' ? 'Этот голос не умеет переозвучку записи. Выберите другой.' : 'Не удалось выбрать голос. Попробуйте ещё раз.'); })
          .finally(() => { if (mounted.current && request.current === current) setBusy(false); });
      },
      value?.id ?? null,
      { only: s2s ? 's2s' : '' },
    );
  }
  return (
    <>
      <button type="button" className="voicepill native-voice-pill" disabled={disabled || busy} aria-busy={busy} onClick={open}>
        <span className="orb">
          <StudioIcon kind="voice" />
        </span>
        <span>
          <b>{uiText(value?.name || 'Выберите голос')}</b>
          <small>{uiText(sub ?? (s2s ? 'Голос для переозвучки' : 'Библиотека и ваши клоны'))}</small>
        </span>
        <StudioIcon kind="chev" />
      </button>
      {error && <p role="alert">{uiText(error)}</p>}
    </>
  );
}
